import { useState, useEffect, useRef } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Loader2, Upload, Trash2, Pencil, Layers, FileText, Plus, Tag, X, Search, Eye, Download
} from "lucide-react";
import { format } from "date-fns";

const fmtDate = (d) => { try { return format(new Date(d), "MMM d, yyyy"); } catch { return d; } };

export default function GalleryConfigPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Edit dialog
  const [editItem, setEditItem] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editTags, setEditTags] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [saving, setSaving] = useState(false);

  // Upload dialog
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadTags, setUploadTags] = useState("");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Delete dialog
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/admin/gallery`, { withCredentials: true });
      setItems(res.data);
    } catch { toast.error("Failed to load gallery items"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleDownload = (item) => {
    // Direct browser nav — keeps the session cookie attached and lets the
    // browser drive the Content-Disposition save. Avoids buffering the PPTX
    // through JS (could be 5–100 MB).
    const a = document.createElement("a");
    a.href = `${API}/admin/gallery/${item.id}/download`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const filtered = items.filter(item => {
    if (!search) return true;
    const s = search.toLowerCase();
    return item.title?.toLowerCase().includes(s) || (item.tags || []).some(t => t.toLowerCase().includes(s));
  });

  const openEdit = (item) => {
    setEditItem(item);
    setEditTitle(item.title || "");
    setEditTags((item.tags || []).join(", "));
    setEditDesc(item.description || "");
  };

  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      const tags = editTags.split(",").map(t => t.trim()).filter(Boolean);
      await axios.put(`${API}/admin/gallery/${editItem.id}`, {
        title: editTitle, tags, description: editDesc
      }, { withCredentials: true });
      toast.success("Gallery item updated");
      setEditItem(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/gallery/${deleteItem.id}`, { withCredentials: true });
      toast.success("Item removed from gallery");
      setDeleteItem(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally { setDeleting(false); }
  };

  const handleUpload = async () => {
    if (!uploadFile) { toast.error("Select a file first"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("title", uploadTitle || uploadFile.name.replace(".pptx", ""));
      formData.append("tags", uploadTags);
      formData.append("description", uploadDesc);
      await axios.post(`${API}/admin/gallery/upload`, formData, { withCredentials: true });
      toast.success("New gallery item created!");
      setShowUpload(false);
      setUploadFile(null); setUploadTitle(""); setUploadTags(""); setUploadDesc("");
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="page-container" data-testid="gallery-config-page">
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
        <span>Admin</span><span>/</span><span className="text-gray-900">Gallery Config</span>
      </div>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope']">Gallery Config</h1>
          <p className="text-sm text-gray-500 mt-1">Manage gallery templates visible to all users ({items.length} items)</p>
        </div>
        <Button onClick={() => { setShowUpload(true); setUploadFile(null); setUploadTitle(""); setUploadTags(""); setUploadDesc(""); }}
          className="bg-black hover:bg-gray-800 text-white" data-testid="add-gallery-item-btn">
          <Plus className="w-4 h-4 mr-1.5" />Add Template
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input placeholder="Search by title or tag..." value={search} onChange={e => setSearch(e.target.value)}
          className="pl-9" data-testid="gallery-config-search" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-400">
          {search ? "No items matching search" : "No gallery items yet. Upload a template to get started."}
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="gallery-config-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Preview</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Title</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Type</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Tags</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Added</th>
                  <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                    data-testid={`gallery-config-row-${item.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="w-16 h-10 bg-gray-100 rounded overflow-hidden flex items-center justify-center">
                        <img src={`${API}/thumbnails/${item.source_id}`} alt="" className="w-full h-full object-cover" loading="lazy"
                          onError={(e) => {
                            // Avoid innerHTML — replace the broken <img> with a
                            // safely-constructed DOM node carrying the placeholder.
                            const parent = e.target.parentElement;
                            if (!parent) return;
                            e.target.style.display = 'none';
                            const placeholder = document.createElement('span');
                            placeholder.className = 'text-gray-300 text-[10px]';
                            placeholder.textContent = 'No preview';
                            parent.replaceChildren(placeholder);
                          }}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <p className="text-xs font-medium text-gray-900 line-clamp-2 max-w-[250px]">{item.title || "Untitled"}</p>
                      {item.description && <p className="text-[10px] text-gray-400 line-clamp-1">{item.description}</p>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 ${
                        item.source_type === "deck" ? "text-indigo-700 bg-indigo-50 border border-indigo-200" : "text-gray-500 bg-gray-100 border border-gray-200"
                      }`}>
                        {item.source_type === "deck" ? <><Layers className="w-2.5 h-2.5" />Deck</> : <><FileText className="w-2.5 h-2.5" />Slide</>}
                        {item.num_slides ? ` (${item.num_slides})` : ""}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {(item.tags || []).slice(0, 4).map(tag => (
                          <span key={tag} className="text-[10px] font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">{tag}</span>
                        ))}
                        {(item.tags || []).length > 4 && <span className="text-[10px] text-gray-400">+{item.tags.length - 4}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtDate(item.created_at)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-1">
                        <Button
                          size="sm" variant="ghost" className="h-7 w-7 p-0"
                          onClick={() => handleDownload(item)}
                          title="Download source PPTX"
                          data-testid={`download-gallery-${item.id}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(item)}
                          data-testid={`edit-gallery-${item.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteItem(item)} data-testid={`delete-gallery-${item.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Gallery Item</DialogTitle>
            <DialogDescription>Update the title, tags, and description for this template</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Title</label>
              <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} placeholder="Template title"
                data-testid="edit-gallery-title" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Tags</label>
              <Input value={editTags} onChange={e => setEditTags(e.target.value)} placeholder="tag1, tag2, tag3"
                data-testid="edit-gallery-tags" />
              <p className="text-[10px] text-gray-400 mt-1">Separate tags with commas</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Description</label>
              <Textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Brief description..."
                className="min-h-[80px]" data-testid="edit-gallery-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-black hover:bg-gray-800"
              data-testid="save-gallery-edit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Gallery Template</DialogTitle>
            <DialogDescription>Upload a PPTX file to add to the gallery</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">PPTX File</label>
              <input ref={fileInputRef} type="file" accept=".pptx" onChange={e => setUploadFile(e.target.files?.[0] || null)} className="hidden" />
              <button onClick={() => fileInputRef.current?.click()}
                className="w-full p-4 border-2 border-dashed border-gray-200 rounded-lg text-center hover:border-gray-400 transition-colors"
                data-testid="upload-file-area">
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-700"><FileText className="w-4 h-4" />{uploadFile.name}</div>
                ) : (
                  <div className="text-sm text-gray-500"><Upload className="w-5 h-5 mx-auto mb-1" />Click to select a PPTX file</div>
                )}
              </button>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Title</label>
              <Input value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} placeholder="Template name"
                data-testid="upload-title" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Tags</label>
              <Input value={uploadTags} onChange={e => setUploadTags(e.target.value)} placeholder="market-analysis, finance, strategy"
                data-testid="upload-tags" />
              <p className="text-[10px] text-gray-400 mt-1">Separate tags with commas</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Description (optional)</label>
              <Textarea value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} placeholder="Brief description..."
                className="min-h-[60px]" data-testid="upload-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadFile} className="bg-black hover:bg-gray-800"
              data-testid="upload-submit-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload & Add to Gallery"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove from Gallery?</DialogTitle>
            <DialogDescription>
              This will remove "{deleteItem?.title}" from the gallery. The original file will not be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteItem(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}
              data-testid="confirm-delete-gallery">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
